// ============================================================
// SALT — Match Browse Page
// Central hub for all match activity. Single API call returns
// personalized sections based on auth state.
// Route: /matches (game subdomain only)
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import axiosClient from '../api/axiosClient';
import { extractError } from '../utils/formatters';

// Dashboard components (reuse SeasonHeader)
import SeasonHeader from '../components/dashboard/SeasonHeader';

// UI
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ToastContainer from '../components/ui/Toast';

// Dashboard helpers (reuse formatters + icons)
import {
  formatMatchType,
  formatMatchFormat,
  getTimeRemaining,
  formatScheduledTime,
  DefaultTeamIcon,
  DefaultTeamIconSmall,
} from '../components/dashboard/DashboardHelpers';

// Styles
import '../styles/match-browse.css';

// —— Types (mirror backend matchesBrowse.types.ts) ———————————

type MatchType = 'available_now' | 'scheduled' | 'direct_challenge';
type MatchFormat = 'bo1' | 'bo3' | 'bo5' | 'bo7';

interface MatchTeamBasic {
  teamId: number;
  teamName: string;
  logoUrl: string | null;
  teamUrl: string;
}

interface MatchTeamWithStats extends MatchTeamBasic {
  rank: number | null;
  points: number;
}

interface YourPendingMatch {
  matchId: number;
  matchType: MatchType;
  matchFormat: MatchFormat;
  scheduledAt: string | null;
  expiresAt: string | null;
  challengedTeam: MatchTeamBasic | null;
  createdAt: string;
  matchUrl: string;
}

interface IncomingChallenge {
  matchId: number;
  matchFormat: MatchFormat;
  expiresAt: string | null;
  challengingTeam: MatchTeamWithStats;
  createdAt: string;
  matchUrl: string;
}

interface AvailableMatch {
  matchId: number;
  matchType: 'available_now' | 'scheduled';
  matchFormat: MatchFormat;
  scheduledAt: string | null;
  expiresAt: string | null;
  creatingTeam: MatchTeamWithStats;
  createdAt: string;
  matchUrl: string;
}

interface ActiveMatchEntry {
  matchId: number;
  matchType: MatchType;
  matchFormat: MatchFormat;
  matchState: 'accepted' | 'in_progress';
  scheduledAt: string | null;
  startedAt: string | null;
  acceptedAt: string | null;
  opponent: MatchTeamWithStats;
  yourTeam: MatchTeamBasic;
  teamAScore: number;
  teamBScore: number;
  isYourTeamA: boolean;
  matchUrl: string;
}

interface LiveMatchStreamInfo {
  platform: 'twitch' | 'youtube' | 'twitter';
  streamUrl: string;
  username: string;
}

interface LiveMatchEntry {
  matchId: number;
  matchType: MatchType;
  matchFormat: MatchFormat;
  matchState: 'accepted' | 'in_progress';
  acceptedAt: string | null;
  startedAt: string | null;
  teamA: MatchTeamWithStats;
  teamB: MatchTeamWithStats;
  streams: LiveMatchStreamInfo[];
  matchUrl: string;
}

interface BrowseUserTeam {
  teamId: number;
  teamName: string;
  role: 'captain' | 'member';
  points: number;
  teamUrl: string;
}

interface MatchesBrowseData {
  yourPendingMatches: YourPendingMatch[];
  incomingChallenges: IncomingChallenge[];
  availableMatches: AvailableMatch[];
  activeMatches: ActiveMatchEntry[];
  liveMatches: LiveMatchEntry[];
  userTeam: BrowseUserTeam | null;
}

// —— Difficulty Indicator —————————————————————————————————————
// Compares opponent points vs your points to show arrow indicators.
// Green up arrows = opponent is higher ranked (harder, more points to gain)
// Red down arrows = opponent is lower ranked (easier, less points to gain)
// Yellow dashes = similar skill level

type DifficultyLevel = 'up-3' | 'up-2' | 'up-1' | 'even' | 'down-1' | 'down-2' | 'down-3';

function getDifficultyLevel(
  opponentPoints: number,
  yourPoints: number
): DifficultyLevel {
  const diff = opponentPoints - yourPoints;
  const absDiff = Math.abs(diff);

  // Thresholds tuned for SALT point system
  if (absDiff < 5) return 'even';        // Within 5 pts = similar skill
  if (absDiff < 12) return diff > 0 ? 'up-1' : 'down-1';   // Small gap
  if (absDiff < 25) return diff > 0 ? 'up-2' : 'down-2';   // Moderate gap
  return diff > 0 ? 'up-3' : 'down-3';                       // Large gap
}

interface DifficultyIndicatorProps {
  level: DifficultyLevel;
}

const DifficultyIndicator: React.FC<DifficultyIndicatorProps> = ({ level }) => {
  if (level === 'even') {
    return (
      <div className="sl-mb-difficulty sl-mb-difficulty--even" title="Similar skill level">
        <span className="sl-mb-diff-dash">―</span>
      </div>
    );
  }

  const isUp = level.startsWith('up');
  const count = parseInt(level.split('-')[1], 10);
  const colorClass = isUp ? 'sl-mb-difficulty--up' : 'sl-mb-difficulty--down';
  const title = isUp
    ? 'Higher rated opponent'
    : 'Lower rated opponent';
  const arrow = isUp ? '▲' : '▼';

  return (
    <div className={`sl-mb-difficulty ${colorClass}`} title={title}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="sl-mb-diff-arrow">{arrow}</span>
      ))}
    </div>
  );
};

// —— Platform Icon Helper ————————————————————————————————————

const PlatformIcon: React.FC<{ platform: 'twitch' | 'youtube' | 'twitter' }> = ({ platform }) => {
  switch (platform) {
    case 'twitch':
      return (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="sl-mb-live-platform-icon">
          <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
        </svg>
      );
    case 'youtube':
      return (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="sl-mb-live-platform-icon">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12z"/>
        </svg>
      );
    case 'twitter':
      return (
        <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="sl-mb-live-platform-icon">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
  }
};

// —— Live Match Countdown ————————————————————————————————————
// Shows "Starts in Xm" / "Starts in Xh Ym" for upcoming matches,
// or "Live" for matches past their start time.

function getLiveMatchCountdown(startedAt: string | null, matchState: string): string | null {
  // Already in progress = live
  if (matchState === 'in_progress') return null;
  if (!startedAt) return null;

  const now = Date.now();
  const start = new Date(startedAt).getTime();
  const diff = start - now;

  // Past start time = playing now
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);

  if (days > 0) return `Starts in ${days}d ${hours}h`;
  if (hours > 0) return `Starts in ${hours}h ${minutes}m`;
  if (minutes > 0) return `Starts in ${minutes}m`;
  return 'Starting now';
}

/**
 * Determine if a live match is currently "playing" (past its start time or in_progress).
 */
function isMatchLive(match: LiveMatchEntry): boolean {
  if (match.matchState === 'in_progress') return true;
  if (!match.startedAt) return false;
  return new Date(match.startedAt).getTime() <= Date.now();
}

/**
 * Sort live matches: playing matches first, then by closest to starting.
 * Within playing matches, sort by most recently started (newest first).
 */
function sortLiveMatches(matches: LiveMatchEntry[]): LiveMatchEntry[] {
  return [...matches].sort((a, b) => {
    const aLive = isMatchLive(a);
    const bLive = isMatchLive(b);

    // Live matches first
    if (aLive && !bLive) return -1;
    if (!aLive && bLive) return 1;

    // Both live: most recently started first
    if (aLive && bLive) {
      const aStart = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bStart = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return bStart - aStart;
    }

    // Both upcoming: closest to starting first
    const aStart = a.startedAt ? new Date(a.startedAt).getTime() : Infinity;
    const bStart = b.startedAt ? new Date(b.startedAt).getTime() : Infinity;
    return aStart - bStart;
  });
}

// —— Accept Modal State ———————————————————————————————————————

interface AcceptModalState {
  isOpen: boolean;
  match: AvailableMatch | IncomingChallenge | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_ACCEPT_MODAL: AcceptModalState = {
  isOpen: false,
  match: null,
  loading: false,
  error: null,
};

// —— Component ————————————————————————————————————————————————

const MatchBrowsePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const { gameId, hasGame, season } = useGame();
  const toast = useToast();

  // Page data
  const [data, setData] = useState<MatchesBrowseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action loading states (keyed by matchId)
  const [decliningId, setDecliningId] = useState<number | null>(null);
  const [cancelingId, setCancelingId] = useState<number | null>(null);

  // Accept modal (shared for available matches + incoming challenges)
  const [acceptModal, setAcceptModal] = useState<AcceptModalState>(INITIAL_ACCEPT_MODAL);

  // Confirmation modals
  const [declineModal, setDeclineModal] = useState<{
    matchId: number;
    teamName: string;
  } | null>(null);
  const [cancelModal, setCancelModal] = useState<{
    matchId: number;
    matchType: MatchType;
  } | null>(null);

  // Refresh key for manual refresh
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Countdown tick — forces re-render every 30s to update "Starts in X" labels
  const [, setCountdownTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setCountdownTick((t) => t + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // Derived state
  const userTeam = data?.userTeam ?? null;
  const isCaptain = userTeam?.role === 'captain';

  // —— Fetch browse data ——————————————————————————————————————

  useEffect(() => {
    if (authLoading) return;
    if (!gameId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchBrowse = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axiosClient.get(
          `/matches/browse?game_id=${gameId}`
        );

        if (cancelled) return;

        const payload = res.data?.data ?? res.data;

        if (payload) {
          setData({
            yourPendingMatches: payload.yourPendingMatches ?? [],
            incomingChallenges: payload.incomingChallenges ?? [],
            availableMatches: payload.availableMatches ?? [],
            activeMatches: payload.activeMatches ?? [],
            liveMatches: payload.liveMatches ?? [],
            userTeam: payload.userTeam ?? null,
          });
        } else {
          setError('Failed to load matches data.');
        }
      } catch (err: any) {
        if (cancelled) return;
        setError(extractError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBrowse();
    return () => {
      cancelled = true;
    };
  }, [gameId, authLoading, refreshKey]);

  // —— Match Actions ——————————————————————————————————————————

  // Open accept modal
  const openAcceptModal = useCallback(
    (match: AvailableMatch | IncomingChallenge) => {
      setAcceptModal({ isOpen: true, match, loading: false, error: null });
    },
    []
  );

  const closeAcceptModal = useCallback(() => {
    setAcceptModal(INITIAL_ACCEPT_MODAL);
  }, []);

  // Confirm accept from modal
  const handleAcceptConfirm = useCallback(async () => {
    if (!acceptModal.match || !userTeam) return;

    setAcceptModal((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await axiosClient.post(`/matches/${acceptModal.match.matchId}/accept`, {
        teamId: userTeam.teamId,
      });
      toast.success('Match Accepted', 'Your match is starting — good luck!');
      setAcceptModal(INITIAL_ACCEPT_MODAL);
      refresh();
    } catch (err: any) {
      const msg = extractError(err);
      setAcceptModal((prev) => ({ ...prev, loading: false, error: msg }));
    }
  }, [acceptModal.match, userTeam, toast, refresh]);

  const handleDecline = async (matchId: number) => {
    if (!userTeam) return;
    setDecliningId(matchId);

    try {
      await axiosClient.post(`/matches/${matchId}/decline`, {
        teamId: userTeam.teamId,
      });
      toast.success('Challenge Declined');
      setDeclineModal(null);
      refresh();
    } catch (err: any) {
      toast.error('Decline Failed', extractError(err));
    } finally {
      setDecliningId(null);
    }
  };

  const handleCancel = async (matchId: number) => {
    if (!userTeam) return;
    setCancelingId(matchId);

    try {
      await axiosClient.post(`/matches/${matchId}/cancel`, {
        teamId: userTeam.teamId,
      });
      toast.success('Match Canceled');
      setCancelModal(null);
      refresh();
    } catch (err: any) {
      toast.error('Cancel Failed', extractError(err));
    } finally {
      setCancelingId(null);
    }
  };

  // —— No game detected ——————————————————————————————————————

  if (!hasGame) {
    return (
      <div className="sl-mb-page">
        <div className="sl-mb-inner">
          <div className="sl-card sl-card--action">
            <h2 className="sl-section-title">No Game Selected</h2>
            <p className="sl-card-subtitle">
              Visit a game-specific subdomain to browse matches.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // —— Accept modal content ———————————————————————————————————

  const renderAcceptModalContent = () => {
    if (!acceptModal.match) return null;

    const match = acceptModal.match;
    const formatLabel = formatMatchFormat(match.matchFormat);

    // Determine match type label
    const typeLabel =
      'matchType' in match
        ? formatMatchType((match as AvailableMatch).matchType)
        : 'Direct Challenge';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="sl-badge sl-badge--format">{formatLabel}</span>
          <span className="sl-badge sl-badge--state">{typeLabel}</span>
        </div>

        {'scheduledAt' in match && match.scheduledAt && (
          <p style={{ fontSize: '0.82rem', color: 'var(--sl-text-muted)', margin: 0 }}>
            Scheduled for{' '}
            {new Date(match.scheduledAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </p>
        )}

        <p style={{ fontSize: '0.85rem', color: 'var(--sl-text)', margin: 0 }}>
          Accept this {formatLabel} {typeLabel.toLowerCase()} match?
          Your team will be locked in once accepted.
        </p>

        {acceptModal.error && (
          <p style={{ fontSize: '0.8rem', color: '#fca5a5', margin: 0 }}>
            {acceptModal.error}
          </p>
        )}
      </div>
    );
  };

  // —— Render —————————————————————————————————————————————————

  return (
    <div className="sl-mb-page">
      <div className="sl-mb-inner">
        {/* Season Header */}
        <SeasonHeader season={season} loading={loading && !data} />

        {/* Page Header + Actions */}
        <div className="sl-mb-page-header">
          <div className="sl-mb-page-header-text">
            <h1 className="sl-mb-page-title">Matches</h1>
            <p className="sl-mb-page-subtitle">
              Browse open matches, manage challenges, and track active series.
            </p>
          </div>
          <div className="sl-mb-page-header-actions">
            <button
              className="sl-mb-refresh-btn"
              onClick={refresh}
              disabled={loading}
              title="Refresh matches"
            >
              <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
                <path
                  d="M21 12a9 9 0 11-2.636-6.364"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M21 3v6h-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Refresh
            </button>

            {isCaptain && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/matches/create')}
              >
                Create Match
              </Button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="sl-dashboard-error">
            <span>{error}</span>
            <button className="sl-dashboard-error-retry" onClick={refresh}>
              Retry
            </button>
          </div>
        )}

        {/* Loading Skeletons */}
        {loading && !data && (
          <div className="sl-mb-sections">
            {[1, 2, 3].map((i) => (
              <div key={i} className="sl-card sl-mb-skeleton-card">
                <div className="sl-skeleton-row sl-skeleton-pulse" style={{ height: 20, width: '40%' }} />
                <div className="sl-skeleton-row sl-skeleton-pulse" style={{ height: 52, marginTop: 12 }} />
                <div className="sl-skeleton-row sl-skeleton-pulse" style={{ height: 52, marginTop: 8 }} />
              </div>
            ))}
          </div>
        )}

        {/* Data loaded */}
        {data && (
          <div className="sl-mb-sections">

            {/* ═══════════════════════════════════════════
                Section 1: Active Matches
               ═══════════════════════════════════════════ */}
            {isAuthenticated && userTeam && data.activeMatches.length > 0 && (
              <section className="sl-card sl-mb-section">
                <div className="sl-card-header">
                  <span className="sl-card-title">Active Matches</span>
                  <span className="sl-mb-count-badge">
                    {data.activeMatches.length}
                  </span>
                </div>

                <div className="sl-mb-list">
                  {data.activeMatches.map((match) => {
                    const yourScore = match.isYourTeamA
                      ? match.teamAScore
                      : match.teamBScore;
                    const oppScore = match.isYourTeamA
                      ? match.teamBScore
                      : match.teamAScore;

                    return (
                      <div
                        key={match.matchId}
                        className="sl-mb-active-card"
                        onClick={() => navigate(match.matchUrl)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && navigate(match.matchUrl)
                        }
                      >
                        {/* Badges */}
                        <div className="sl-mb-active-badges">
                          <span className="sl-badge sl-badge--format">
                            {formatMatchFormat(match.matchFormat)}
                          </span>
                          <span
                            className={`sl-badge ${
                              match.matchState === 'in_progress'
                                ? 'sl-badge--state-live'
                                : 'sl-badge--state'
                            }`}
                          >
                            {match.matchState === 'in_progress'
                              ? 'In Progress'
                              : 'Accepted'}
                          </span>
                        </div>

                        {/* VS Layout */}
                        <div className="sl-mb-active-versus">
                          {/* Your Team */}
                          <div className="sl-mb-active-team">
                            <div className="sl-mb-team-logo-wrap">
                              {match.yourTeam.logoUrl ? (
                                <img
                                  src={match.yourTeam.logoUrl}
                                  alt=""
                                  className="sl-mb-team-logo"
                                />
                              ) : (
                                <DefaultTeamIcon />
                              )}
                            </div>
                            <div className="sl-mb-active-team-info">
                              <span className="sl-mb-active-team-name">
                                {match.yourTeam.teamName}
                              </span>
                              <span className="sl-mb-active-team-label">
                                Your Team
                              </span>
                            </div>
                          </div>

                          {/* Score */}
                          <div className="sl-mb-active-score">
                            <span className="sl-mb-active-score-num">
                              {yourScore}
                            </span>
                            <span className="sl-mb-active-score-sep">-</span>
                            <span className="sl-mb-active-score-num">
                              {oppScore}
                            </span>
                          </div>

                          {/* Opponent */}
                          <div className="sl-mb-active-team sl-mb-active-team--right">
                            <div className="sl-mb-active-team-info sl-mb-active-team-info--right">
                              <span className="sl-mb-active-team-name">
                                {match.opponent.teamName}
                              </span>
                              {match.opponent.rank != null && (
                                <span className="sl-mb-active-team-rank">
                                  #{match.opponent.rank}
                                </span>
                              )}
                            </div>
                            <div className="sl-mb-team-logo-wrap">
                              {match.opponent.logoUrl ? (
                                <img
                                  src={match.opponent.logoUrl}
                                  alt=""
                                  className="sl-mb-team-logo"
                                />
                              ) : (
                                <DefaultTeamIcon />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="sl-mb-active-footer">
                          <span className="sl-mb-meta-text">
                            {formatMatchType(match.matchType)}
                          </span>
                          <span className="sl-mb-link-text">
                            View Details &rarr;
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ═══════════════════════════════════════════
                Section 2: Incoming Challenges
               ═══════════════════════════════════════════ */}
            {isAuthenticated &&
              userTeam &&
              isCaptain &&
              data.incomingChallenges.length > 0 && (
                <section className="sl-card sl-mb-section">
                  <div className="sl-card-header">
                    <span className="sl-card-title">Incoming Challenges</span>
                    <span className="sl-mb-count-badge sl-mb-count-badge--alert">
                      {data.incomingChallenges.length}
                    </span>
                  </div>

                  <div className="sl-mb-list">
                    {data.incomingChallenges.map((challenge) => (
                      <div
                        key={challenge.matchId}
                        className="sl-mb-challenge-row"
                      >
                        <div
                          className="sl-mb-challenge-info"
                          onClick={() => navigate(challenge.matchUrl)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === 'Enter' &&
                            navigate(challenge.matchUrl)
                          }
                        >
                          <div className="sl-mb-team-logo-wrap sl-mb-team-logo-wrap--sm">
                            {challenge.challengingTeam.logoUrl ? (
                              <img
                                src={challenge.challengingTeam.logoUrl}
                                alt=""
                                className="sl-mb-team-logo"
                              />
                            ) : (
                              <DefaultTeamIconSmall />
                            )}
                          </div>

                          <div className="sl-mb-challenge-details">
                            <div className="sl-mb-challenge-team-line">
                              <span className="sl-mb-row-team-name">
                                {challenge.challengingTeam.teamName}
                              </span>
                              {challenge.challengingTeam.rank != null && (
                                <span className="sl-mb-row-rank">
                                  #{challenge.challengingTeam.rank}
                                </span>
                              )}
                              <span className="sl-mb-row-points">
                                {challenge.challengingTeam.points.toFixed(1)} pts
                              </span>
                            </div>
                            <div className="sl-mb-challenge-meta">
                              <span className="sl-badge sl-badge--format">
                                {formatMatchFormat(challenge.matchFormat)}
                              </span>
                              <span className="sl-badge sl-badge--type-challenge">
                                Direct Challenge
                              </span>
                              {challenge.expiresAt && (
                                <span className="sl-mb-time-pill">
                                  {getTimeRemaining(challenge.expiresAt)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="sl-mb-challenge-actions">
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={acceptModal.isOpen}
                            onClick={() => openAcceptModal(challenge)}
                          >
                            Accept
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            isLoading={decliningId === challenge.matchId}
                            disabled={decliningId !== null}
                            onClick={() =>
                              setDeclineModal({
                                matchId: challenge.matchId,
                                teamName:
                                  challenge.challengingTeam.teamName,
                              })
                            }
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* ═══════════════════════════════════════════
                Section 3: Your Pending Matches
               ═══════════════════════════════════════════ */}
            {isAuthenticated &&
              userTeam &&
              data.yourPendingMatches.length > 0 && (
                <section className="sl-card sl-mb-section">
                  <div className="sl-card-header">
                    <span className="sl-card-title">Your Pending Matches</span>
                    <span className="sl-mb-count-badge">
                      {data.yourPendingMatches.length}
                    </span>
                  </div>

                  <div className="sl-mb-list">
                    {data.yourPendingMatches.map((match) => (
                      <div key={match.matchId} className="sl-mb-pending-row">
                        <div
                          className="sl-mb-pending-info"
                          onClick={() => navigate(match.matchUrl)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === 'Enter' && navigate(match.matchUrl)
                          }
                        >
                          <div className="sl-mb-pending-details">
                            <div className="sl-mb-pending-top-line">
                              {match.challengedTeam ? (
                                <>
                                  <span className="sl-mb-row-team-name">
                                    vs {match.challengedTeam.teamName}
                                  </span>
                                </>
                              ) : (
                                <span className="sl-mb-row-team-name">
                                  Open Challenge
                                </span>
                              )}
                            </div>
                            <div className="sl-mb-pending-meta">
                              <span className="sl-badge sl-badge--format">
                                {formatMatchFormat(match.matchFormat)}
                              </span>
                              <span className="sl-badge sl-badge--type">
                                {formatMatchType(match.matchType)}
                              </span>
                              {match.matchType === 'scheduled' &&
                              match.scheduledAt ? (
                                <span className="sl-mb-time-pill">
                                  {formatScheduledTime(match.scheduledAt)}
                                </span>
                              ) : match.expiresAt ? (
                                <span className="sl-mb-time-pill">
                                  {getTimeRemaining(match.expiresAt)}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        {isCaptain && (
                          <div className="sl-mb-pending-actions">
                            <Button
                              variant="ghost"
                              size="sm"
                              isLoading={cancelingId === match.matchId}
                              disabled={cancelingId !== null}
                              onClick={() =>
                                setCancelModal({
                                  matchId: match.matchId,
                                  matchType: match.matchType,
                                })
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

            {/* ═══════════════════════════════════════════
                Section 4: Available Matches
                Team identity hidden for competitive integrity.
                Difficulty arrows + Accept button on right side.
               ═══════════════════════════════════════════ */}
            <section className="sl-card sl-mb-section">
              <div className="sl-card-header">
                <span className="sl-card-title">Available Matches</span>
                {data.availableMatches.length > 0 && (
                  <span className="sl-mb-count-badge">
                    {data.availableMatches.length}
                  </span>
                )}
              </div>

              {/* Difficulty legend — only when logged in with a team and matches exist */}
              {userTeam && data.availableMatches.length > 0 && (
                <div className="sl-mb-difficulty-legend">
                  <span className="sl-mb-legend-item sl-mb-legend-item--up">
                    <span className="sl-mb-diff-arrow">▲</span> Higher Rated
                  </span>
                  <span className="sl-mb-legend-sep">·</span>
                  <span className="sl-mb-legend-item sl-mb-legend-item--even">
                    <span className="sl-mb-diff-dash">―</span> Even
                  </span>
                  <span className="sl-mb-legend-sep">·</span>
                  <span className="sl-mb-legend-item sl-mb-legend-item--down">
                    <span className="sl-mb-diff-arrow">▼</span> Lower Rated
                  </span>
                </div>
              )}

              {data.availableMatches.length === 0 ? (
                <div className="sl-mb-empty">
                  <div className="sl-mb-empty-title">
                    No open matches right now
                  </div>
                  <div className="sl-mb-empty-text">
                    {isCaptain
                      ? 'Be the first to post an Available Now match or schedule a future series.'
                      : isAuthenticated && userTeam
                      ? 'Check back soon or ask your captain to create a match.'
                      : 'Check back soon — teams are competing all the time.'}
                  </div>
                  {isCaptain && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate('/matches/create')}
                      className="sl-mb-empty-btn"
                    >
                      Create Match
                    </Button>
                  )}
                </div>
              ) : (
                <div className="sl-mb-list">
                  {data.availableMatches.map((match) => {
                    // Calculate difficulty relative to user's team
                    const difficulty =
                      userTeam != null
                        ? getDifficultyLevel(
                            match.creatingTeam.points,
                            userTeam.points
                          )
                        : null;

                    return (
                      <div key={match.matchId} className="sl-mb-available-row">
                        <div className="sl-mb-available-info">
                          <div className="sl-mb-available-details">
                            <div className="sl-mb-available-team-line">
                              <span className="sl-mb-row-team-name">
                                Open Challenge
                              </span>
                            </div>
                            <div className="sl-mb-available-meta">
                              <span className="sl-badge sl-badge--format">
                                {formatMatchFormat(match.matchFormat)}
                              </span>
                              <span className="sl-badge sl-badge--type">
                                {formatMatchType(match.matchType)}
                              </span>
                              {match.matchType === 'scheduled' &&
                              match.scheduledAt ? (
                                <span className="sl-mb-time-pill">
                                  {formatScheduledTime(match.scheduledAt)}
                                </span>
                              ) : match.expiresAt ? (
                                <span className="sl-mb-time-pill">
                                  {getTimeRemaining(match.expiresAt)}
                                </span>
                              ) : null}
                            </div>
                          </div>

                          {/* Difficulty indicator inside the card */}
                          {difficulty && (
                            <DifficultyIndicator level={difficulty} />
                          )}
                        </div>

                        {/* Accept button outside the info area */}
                        {isCaptain && (
                          <div className="sl-mb-available-actions">
                            <Button
                              variant="primary"
                              size="sm"
                              disabled={acceptModal.isOpen}
                              onClick={() => openAcceptModal(match)}
                            >
                              Accept
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ═══════════════════════════════════════════
                CTAs for guests / no-team / members
               ═══════════════════════════════════════════ */}
            {!isAuthenticated && (
              <div className="sl-mb-cta">
                <div className="sl-mb-cta-inner">
                  <h3 className="sl-mb-cta-title">Ready to Compete?</h3>
                  <p className="sl-mb-cta-text">
                    Sign in to accept matches and challenge other teams.
                  </p>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => navigate('/auth')}
                  >
                    Sign In to Compete
                  </Button>
                </div>
              </div>
            )}

            {isAuthenticated && !userTeam && (
              <div className="sl-mb-cta">
                <div className="sl-mb-cta-inner">
                  <h3 className="sl-mb-cta-title">Join the Ladder</h3>
                  <p className="sl-mb-cta-text">
                    Create or join a team to start competing this season.
                  </p>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => navigate('/create-team')}
                  >
                    Create a Team
                  </Button>
                </div>
              </div>
            )}

            {isAuthenticated && userTeam && !isCaptain && (
              <div className="sl-mb-info-banner">
                Only team captains can accept, decline, or create matches.
              </div>
            )}

            {/* ═══════════════════════════════════════════
                Section 5: Live on SALT
                All accepted/in_progress matches for this game/season.
                Visible to everyone (including guests).
                Sorted: live matches first, then by closest to starting.
               ═══════════════════════════════════════════ */}
            <section className="sl-card sl-mb-section sl-mb-live-section">
              <div className="sl-card-header">
                <span className="sl-card-title">Live on SALT</span>
                {data.liveMatches.length > 0 && (
                  <span className="sl-mb-count-badge">
                    {data.liveMatches.length}
                  </span>
                )}
              </div>

              {data.liveMatches.length === 0 ? (
                <div className="sl-mb-empty">
                  <div className="sl-mb-empty-title">
                    No live matches right now
                  </div>
                  <div className="sl-mb-empty-text">
                    Check back soon — teams are competing all the time.
                  </div>
                </div>
              ) : (
                <div className="sl-mb-live-grid">
                  {sortLiveMatches(data.liveMatches).map((match) => {
                    const matchIsLive = isMatchLive(match);
                    const countdown = getLiveMatchCountdown(match.startedAt, match.matchState);

                    return (
                      <div
                        key={match.matchId}
                        className={`sl-mb-live-card${matchIsLive ? ' sl-mb-live-card--active' : ''}`}
                        onClick={() => navigate(match.matchUrl)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' && navigate(match.matchUrl)
                        }
                      >
                        {/* Top row: badges + status/countdown */}
                        <div className="sl-mb-live-top">
                          <div className="sl-mb-live-badges">
                            <span className="sl-badge sl-badge--format">
                              {formatMatchFormat(match.matchFormat)}
                            </span>
                            <span className="sl-badge sl-badge--type">
                              {formatMatchType(match.matchType)}
                            </span>
                            {matchIsLive ? (
                              <span className="sl-badge sl-badge--state-live">
                                Playing Now
                              </span>
                            ) : countdown ? (
                              <span className="sl-mb-live-countdown">
                                {countdown}
                              </span>
                            ) : null}
                          </div>

                          {match.streams.length > 0 && (
                            <div className="sl-mb-live-indicator">
                              <span className="sl-mb-live-dot" />
                              <span className="sl-mb-live-label">
                                Player Streaming
                              </span>
                            </div>
                          )}
                        </div>

                        {/* VS Layout */}
                        <div className="sl-mb-live-versus">
                          {/* Team A */}
                          <div className="sl-mb-live-team">
                            <div className="sl-mb-team-logo-wrap">
                              {match.teamA.logoUrl ? (
                                <img
                                  src={match.teamA.logoUrl}
                                  alt=""
                                  className="sl-mb-team-logo"
                                />
                              ) : (
                                <DefaultTeamIcon />
                              )}
                            </div>
                            <div className="sl-mb-live-team-info">
                              <span className="sl-mb-live-team-name">
                                {match.teamA.teamName}
                              </span>
                              {match.teamA.rank != null && (
                                <span className="sl-mb-live-team-rank">
                                  #{match.teamA.rank}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* VS divider */}
                          <div className="sl-mb-live-vs-badge">VS</div>

                          {/* Team B */}
                          <div className="sl-mb-live-team sl-mb-live-team--right">
                            <div className="sl-mb-live-team-info sl-mb-live-team-info--right">
                              <span className="sl-mb-live-team-name">
                                {match.teamB.teamName}
                              </span>
                              {match.teamB.rank != null && (
                                <span className="sl-mb-live-team-rank">
                                  #{match.teamB.rank}
                                </span>
                              )}
                            </div>
                            <div className="sl-mb-team-logo-wrap">
                              {match.teamB.logoUrl ? (
                                <img
                                  src={match.teamB.logoUrl}
                                  alt=""
                                  className="sl-mb-team-logo"
                                />
                              ) : (
                                <DefaultTeamIcon />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Footer: stream links or view details */}
                        <div className="sl-mb-live-footer">
                          {match.streams.length > 0 ? (
                            <div className="sl-mb-live-streams">
                              {match.streams.map((stream, idx) => {
                                // Security: only render as clickable link if URL uses https://
                                const isValidUrl = stream.streamUrl?.startsWith('https://');
                                return isValidUrl ? (
                                  <a
                                    key={idx}
                                    href={stream.streamUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="sl-mb-live-stream-link"
                                    onClick={(e) => e.stopPropagation()}
                                    title={`Watch ${stream.username} on ${stream.platform}`}
                                  >
                                    <PlatformIcon platform={stream.platform} />
                                    <span>{stream.username}</span>
                                  </a>
                                ) : (
                                  <span
                                    key={idx}
                                    className="sl-mb-live-stream-link"
                                    title={`${stream.username} on ${stream.platform}`}
                                  >
                                    <PlatformIcon platform={stream.platform} />
                                    <span>{stream.username}</span>
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <span className="sl-mb-meta-text">
                              {matchIsLive ? 'Playing Now' : 'Upcoming'}
                            </span>
                          )}
                          <span className="sl-mb-link-text">
                            View Match &rarr;
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}

        {/* —— Accept Match Modal ———————————————————————— */}
        <Modal
          isOpen={acceptModal.isOpen}
          title="Accept Match"
          confirmLabel="Accept Match"
          cancelLabel="Close"
          onConfirm={handleAcceptConfirm}
          onCancel={closeAcceptModal}
          onClose={closeAcceptModal}
          disableConfirm={acceptModal.loading}
          disableCancel={acceptModal.loading}
        >
          {renderAcceptModalContent()}
        </Modal>

        {/* —— Decline Challenge Modal ————————————————————— */}
        <Modal
          isOpen={declineModal !== null}
          title="Decline Challenge"
          confirmLabel="Decline"
          onConfirm={() =>
            declineModal && handleDecline(declineModal.matchId)
          }
          onCancel={() => setDeclineModal(null)}
          onClose={() => setDeclineModal(null)}
          disableConfirm={decliningId !== null}
        >
          <p className="sl-modal-text">
            Are you sure you want to decline this challenge from{' '}
            <strong>{declineModal?.teamName}</strong>? This cannot be undone.
          </p>
        </Modal>

        {/* —— Cancel Match Modal ——————————————————————————— */}
        <Modal
          isOpen={cancelModal !== null}
          title="Cancel Match"
          confirmLabel="Cancel Match"
          onConfirm={() =>
            cancelModal && handleCancel(cancelModal.matchId)
          }
          onCancel={() => setCancelModal(null)}
          onClose={() => setCancelModal(null)}
          disableConfirm={cancelingId !== null}
        >
          <p className="sl-modal-text">
            Are you sure you want to cancel this{' '}
            {cancelModal
              ? formatMatchType(cancelModal.matchType).toLowerCase()
              : ''}{' '}
            match? This cannot be undone.
          </p>
        </Modal>

        {/* Toast container */}
        <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
      </div>
    </div>
  );
};

export default MatchBrowsePage;